/* eslint-disable jsx-a11y/click-events-have-key-events */

/* eslint-disable jsx-a11y/no-static-element-interactions */
import 'components/common/date-range-picker/styles/default.css'
import 'components/common/date-range-picker/styles/calendar.css'
import styles from './index.module.css'
import clsx from 'clsx'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Calendar } from 'react-date-range'
import ReactDOM from 'react-dom'
import useClickOutside from 'hooks/use-click-outside'

const getScrollParent = (node: any) => {
  const overflowY =
    node instanceof HTMLElement && window.getComputedStyle(node).overflowY
  const isScrollable = overflowY !== 'visible' && overflowY !== 'hidden'

  if (!node) {
    return null
  } else if (isScrollable && node.scrollHeight >= node.clientHeight) {
    return node
  }

  return getScrollParent(node.parentNode) || document.body
}

type CalendarPosition = {
  left: number
  top: number
}
type UseCalendarReturn = {
  focused: {
    current: boolean
  }
  show: () => void
  hide: () => void
  render: () => React.ReactNode
  onChange: (value?: Date) => void
}
export const useCalendar = (
  anchorRef: {
    current: HTMLElement | null | undefined
  },
  value: Date | null | undefined,
  onChange: (value?: Date) => void,
  minDate: Date | null | undefined,
  maxDate: Date | null | undefined,
): UseCalendarReturn => {
  const portalContainerRef = useRef<HTMLElement | null | undefined>(null)
  useEffect(() => {
    portalContainerRef.current =
      document.getElementById('app') || document.getElementById('__next')
  }, [])
  const focused = useRef<boolean>(false)
  const [visible, setVisible] = useState<boolean>(false)

  const [position, setPosition] = useState<
    CalendarPosition | null | undefined
  >()
  useEffect(() => {
    if (!visible) return
    const scrollParentRef = getScrollParent(anchorRef.current)
    if (!scrollParentRef) return

    const handleScroll = () => {
      setVisible(false)
    }

    scrollParentRef.addEventListener('scroll', handleScroll)
    return () => {
      scrollParentRef.removeEventListener('scroll', handleScroll)
    }
  }, [anchorRef, visible])
  const setCalendarRef = useCallback(
    (node: HTMLDivElement | null | undefined) => {
      const calendar = node?.getBoundingClientRect()
      const container = anchorRef.current?.getBoundingClientRect()

      if (calendar && container) {
        if (
          container.top > calendar.height &&
          container.bottom + calendar.height > window.innerHeight
        ) {
          setPosition({
            left: container.left,
            top: container.top - calendar.height,
          })
        } else {
          setPosition({
            left: container.left,
            top: container.bottom,
          })
        }
      }
    },
    [anchorRef],
  )
  const setDate = useCallback(
    (date?: Date) => {
      onChange(date)
      focused.current = false
      setVisible(false)
    },
    [onChange],
  )
  useClickOutside(anchorRef, () => {
    focused.current = false
  })

  const renderPortal = (container: HTMLElement) => {
    return ReactDOM.createPortal(
      <div
        className={clsx(styles.calendar, 'calendar')}
        ref={setCalendarRef}
        style={position ?? undefined}
        onMouseEnter={() => {
          focused.current = true
        }}
        onMouseLeave={() => {
          focused.current = false
        }}
        onClick={event => event.stopPropagation()}
      >
        <Calendar
          date={value}
          onChange={setDate}
          minDate={minDate}
          maxDate={maxDate}
        />
      </div>,
      container,
    )
  }

  const render = () => {
    return (
      <>
        {visible &&
          portalContainerRef.current &&
          renderPortal(portalContainerRef.current)}
      </>
    )
  }

  const show = () => {
    setVisible(true)
  }

  const hide = () => {
    setVisible(false)
  }

  return {
    focused,
    show,
    hide,
    render,
    onChange,
  }
}
